import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { loadReferences } from '../lib/data';
import { errorMessage } from '../lib/errors';
import { supabase } from '../lib/supabase';
import type { ContentPack, EquivalenceRule, Language } from '../lib/types';

type RuleDraft = Omit<EquivalenceRule, 'variants' | 'created_at' | 'updated_at'> & {
  variants: string;
};

const EMPTY_RULE: RuleDraft = {
  id: '',
  content_pack_id: '',
  puzzle_id: null,
  language_code: '',
  scope: 'global',
  group_key: '',
  variants: '',
  rule_type: '',
  sort_order: 0,
  is_active: true,
};

export function EquivalenceRulesPage() {
  const [rules, setRules] = useState<EquivalenceRule[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [packs, setPacks] = useState<ContentPack[]>([]);
  const [languageFilter, setLanguageFilter] = useState('');
  const [packFilter, setPackFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [draft, setDraft] = useState<RuleDraft>(EMPTY_RULE);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('equivalence_rules')
        .select('*')
        .order('sort_order')
        .order('id');
      if (languageFilter) query = query.eq('language_code', languageFilter);
      if (packFilter) query = query.eq('content_pack_id', packFilter);
      if (scopeFilter.trim()) query = query.eq('scope', scopeFilter.trim());
      if (activeFilter !== 'all') query = query.eq('is_active', activeFilter === 'active');
      const { data, error: queryError } = await query;
      if (queryError) throw queryError;
      setRules((data ?? []) as EquivalenceRule[]);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, languageFilter, packFilter, scopeFilter]);

  useEffect(() => {
    void loadReferences()
      .then((references) => {
        setLanguages(references.languages);
        setPacks(references.packs);
        setDraft((current) => ({
          ...current,
          language_code: current.language_code || references.languages[0]?.code || '',
          content_pack_id: current.content_pack_id || references.packs[0]?.id || '',
        }));
      })
      .catch((caught) => setError(errorMessage(caught)));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadRules(), 200);
    return () => window.clearTimeout(timer);
  }, [loadRules]);

  const resetForm = () => {
    setEditingId(null);
    setDraft({
      ...EMPTY_RULE,
      language_code: languages[0]?.code ?? '',
      content_pack_id: packs[0]?.id ?? '',
    });
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    try {
      const payload: EquivalenceRule = {
        id: draft.id.trim(),
        content_pack_id: draft.content_pack_id,
        puzzle_id: draft.puzzle_id?.trim() || null,
        language_code: draft.language_code,
        scope: draft.scope.trim() || 'global',
        group_key: draft.group_key.trim(),
        variants: draft.variants.split(/\r?\n/).map((item) => item.trim()).filter(Boolean),
        rule_type: draft.rule_type.trim(),
        sort_order: draft.sort_order,
        is_active: draft.is_active,
      };
      const query = editingId
        ? supabase.from('equivalence_rules').update(payload).eq('id', editingId)
        : supabase.from('equivalence_rules').insert(payload);
      const { error: queryError } = await query;
      if (queryError) throw queryError;
      setSuccess(editingId ? 'Règle mise à jour.' : 'Règle créée.');
      resetForm();
      await loadRules();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const edit = (rule: EquivalenceRule) => {
    setEditingId(rule.id);
    setDraft({ ...rule, variants: rule.variants.join('\n') });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggle = async (rule: EquivalenceRule) => {
    setError('');
    const { error: queryError } = await supabase
      .from('equivalence_rules')
      .update({ is_active: !rule.is_active })
      .eq('id', rule.id);
    if (queryError) setError(errorMessage(queryError));
    else await loadRules();
  };

  const remove = async (rule: EquivalenceRule) => {
    if (!window.confirm(`Supprimer définitivement la règle « ${rule.id} » ?`)) return;
    setError('');
    const { error: queryError } = await supabase
      .from('equivalence_rules')
      .delete()
      .eq('id', rule.id);
    if (queryError) setError(errorMessage(queryError));
    else {
      if (editingId === rule.id) resetForm();
      await loadRules();
    }
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div><p className="eyebrow">Correspondances éditoriales</p><h1>Règles d’équivalence</h1><p>Gère les variantes reconnues globalement ou pour une énigme donnée.</p></div>
      </header>

      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}

      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow">{editingId ? 'Modification' : 'Création'}</p><h2>{editingId || 'Nouvelle règle'}</h2></div></div>
        <form className="pack-create-form" onSubmit={(event) => void save(event)}>
          <label>Identifiant<input required pattern="[a-z0-9_]+" value={draft.id} onChange={(event) => setDraft({ ...draft, id: event.target.value.trim().toLowerCase() })} /></label>
          <label>Pack<select required value={draft.content_pack_id} onChange={(event) => setDraft({ ...draft, content_pack_id: event.target.value })}>{packs.map((pack) => <option key={pack.id} value={pack.id}>{pack.name}</option>)}</select></label>
          <label>Énigme (facultatif)<input value={draft.puzzle_id ?? ''} onChange={(event) => setDraft({ ...draft, puzzle_id: event.target.value || null })} /></label>
          <label>Langue<select required value={draft.language_code} onChange={(event) => setDraft({ ...draft, language_code: event.target.value })}>{languages.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}</select></label>
          <label>Scope<input required value={draft.scope} onChange={(event) => setDraft({ ...draft, scope: event.target.value })} /></label>
          <label>Clé de groupe<input required value={draft.group_key} onChange={(event) => setDraft({ ...draft, group_key: event.target.value })} /></label>
          <label>Type de règle<input required value={draft.rule_type} onChange={(event) => setDraft({ ...draft, rule_type: event.target.value })} /></label>
          <label>Ordre<input required type="number" value={draft.sort_order} onChange={(event) => setDraft({ ...draft, sort_order: Number(event.target.value) })} /></label>
          <label className="span-2">Variantes (une par ligne)<textarea required rows={6} value={draft.variants} onChange={(event) => setDraft({ ...draft, variants: event.target.value })} /></label>
          <label className="checkbox-field"><input type="checkbox" checked={draft.is_active} onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })} /> Active</label>
          <div className="header-actions"><button className="button primary">{editingId ? 'Enregistrer' : 'Créer la règle'}</button>{editingId && <button type="button" className="button ghost" onClick={resetForm}>Annuler</button>}</div>
        </form>
      </section>

      <section className="panel filter-panel">
        <div className="filter-grid">
          <label>Langue<select value={languageFilter} onChange={(event) => setLanguageFilter(event.target.value)}><option value="">Toutes</option>{languages.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}</select></label>
          <label>Pack<select value={packFilter} onChange={(event) => setPackFilter(event.target.value)}><option value="">Tous</option>{packs.map((pack) => <option key={pack.id} value={pack.id}>{pack.name}</option>)}</select></label>
          <label>Scope<input value={scopeFilter} onChange={(event) => setScopeFilter(event.target.value)} placeholder="global" /></label>
          <label>Statut<select value={activeFilter} onChange={(event) => setActiveFilter(event.target.value)}><option value="all">Tous</option><option value="active">Actives</option><option value="inactive">Inactives</option></select></label>
        </div>
      </section>

      <section className="panel table-panel">
        <div className="panel-heading"><div><p className="eyebrow">Catalogue</p><h2>{rules.length} règle(s)</h2></div></div>
        <div className="table-scroll"><table><thead><tr><th>Règle</th><th>Pack / langue</th><th>Scope</th><th>Variantes</th><th>Statut</th><th aria-label="Actions" /></tr></thead><tbody>
          {loading ? <tr><td colSpan={6} className="empty-cell">Chargement…</td></tr> : rules.length === 0 ? <tr><td colSpan={6} className="empty-cell">Aucune règle ne correspond aux filtres.</td></tr> : rules.map((rule) => <tr key={rule.id}>
            <td><strong>{rule.group_key}</strong><span className="row-subtitle">{rule.id} · {rule.rule_type}</span></td>
            <td>{rule.content_pack_id}<span className="row-subtitle">{rule.language_code}{rule.puzzle_id ? ` · ${rule.puzzle_id}` : ''}</span></td>
            <td>{rule.scope}<span className="row-subtitle">ordre {rule.sort_order}</span></td>
            <td>{rule.variants.join(' · ') || '—'}</td>
            <td>{rule.is_active ? 'Active' : 'Inactive'}</td>
            <td className="row-actions"><button className="button ghost small" onClick={() => edit(rule)}>Modifier</button><button className="button ghost small" onClick={() => void toggle(rule)}>{rule.is_active ? 'Désactiver' : 'Activer'}</button><button className="button danger ghost small" onClick={() => void remove(rule)}>Supprimer</button></td>
          </tr>) }
        </tbody></table></div>
      </section>
    </div>
  );
}
